import { FC, ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import {
	Alert,
	Autosuggest,
	Box,
	Button,
	ColumnLayout,
	Container,
	ExpandableSection,
	FormField,
	Header,
	Input,
	RadioGroup,
	Select,
	SpaceBetween,
	StatusIndicator,
	Table,
	Toggle
} from '@cloudscape-design/components'

import FormInput from '../../ControlledFormInputs/Input'
import useDevelopCommonNetwork from '../../../hooks/query/useDevelopCommonNetwork'

import { useApprovalFormStore } from '../../../stores/gcpProjects/approvalFormStore'
import CONSTANTS from '../../../constants'
import {
	cidrRules,
	countCompleteCommonNetworkSubnets,
	getCommonNetworkSubnetErrors,
	getSubnetCidrError,
	getSubnetPurposeError,
	inheritsCommonNetwork,
	isGcpProjectGke,
	isPsaResourceType,
	normalizeAdditionalSubnets,
	normalizeCommonNetworkResources,
	PSC_RESOURCE_TYPE,
	requiresSubnetPurpose,
	resolveGcpProjectInfrastructureType,
	validateSubnetDetailsForApproval
} from '../../../helper/gcpProjectSubnetValidation'
import type {
	AdditionalSubnet,
	CommonNetworkResource,
	CommonNetworkSubnet,
	gcpProject
} from '../../../types/gcpProject'

type SubnetFormValues = {
	primarySubnet: string
	controlPlaneSubnet: string
	secondaryPodsRange: string
	secondaryServicesRange: string
}

const trimValue = (value: unknown): string =>
	typeof value === 'string' ? value.trim() : ''

const resourceTypeOptions = CONSTANTS.SUBNET_RESOURCE_TYPE_OPTIONS
const commonNetworkResourceTypeOptions =
	CONSTANTS.COMMON_NETWORK_RESOURCE_TYPE_OPTIONS
const psaPurposeOptions = CONSTANTS.PSA_PURPOSE_OPTIONS
const pscPurposeOptions = CONSTANTS.PSC_PURPOSE_OPTIONS

type CommonNetworkVpcState = {
	enabled: boolean
	expanded: boolean
	subnets: CommonNetworkSubnet[]
}

const disabledCommonNetworkVpc: CommonNetworkVpcState = {
	enabled: false,
	expanded: false,
	subnets: []
}

// Invisible label (and description) so a row's remove button lines up with its inputs.
const hiddenFieldText = <span aria-hidden="true">{'\u00a0'}</span>

const renderRowAction = (action: ReactNode, withDescription = false) => (
	<FormField
		label={hiddenFieldText}
		description={withDescription ? hiddenFieldText : undefined}
	>
		{action}
	</FormField>
)

// Common Network subnets are PSC only, so new rows start as PSC.
const emptyCommonNetworkSubnet = (): CommonNetworkSubnet => ({
	cidr: '',
	resourceType: PSC_RESOURCE_TYPE,
	purpose: ''
})

const GcpProjectSubnetDetails: FC<{
	gcpProject: gcpProject
	embeddedInApproval?: boolean
	onSubnetValidityChange?: (isValid: boolean) => void
}> = ({ gcpProject, embeddedInApproval = false, onSubnetValidityChange }) => {
	const details = gcpProject.details || {}
	const infrastructureType = resolveGcpProjectInfrastructureType(details)
	// The environment chosen on the request (Develop, Staging, Production).
	const environment = typeof gcpProject.type === 'string' ? gcpProject.type : ''
	const environmentNetworkHeading = environment
		? `${environment} Network`
		: 'Project Network'
	const inheritsFromDevelop = inheritsCommonNetwork(environment)
	const developCommonNetwork = useDevelopCommonNetwork(
		gcpProject,
		embeddedInApproval
	)
	const isCommonNetworkInherited =
		developCommonNetwork.status !== 'not_applicable'
	const isDevelopRequestMissing = developCommonNetwork.status === 'not_found'
	const isGke = isGcpProjectGke(details)
	const savedAdditionalSubnets = useMemo(
		() => normalizeAdditionalSubnets(details.additionalSubnets),
		[details.additionalSubnets]
	)
	const savedPrimarySubnetResourceType =
		typeof details.primarySubnetResourceType === 'string'
			? details.primarySubnetResourceType
			: ''
	const savedPrimarySubnetPurpose =
		typeof details.primarySubnetPurpose === 'string'
			? details.primarySubnetPurpose
			: ''
	const hasSavedSubnets =
		Boolean(trimValue(details.primarySubnet)) ||
		savedAdditionalSubnets.length > 0

	const [nonGkeAddSubnets, setNonGkeAddSubnets] = useState<'yes' | 'no'>(
		hasSavedSubnets ? 'yes' : 'no'
	)
	const [editableAdditionalSubnets, setEditableAdditionalSubnets] = useState<
		AdditionalSubnet[]
	>(savedAdditionalSubnets)
	const [
		editablePrimarySubnetResourceType,
		setEditablePrimarySubnetResourceType
	] = useState(savedPrimarySubnetResourceType)
	const [editablePrimarySubnetPurpose, setEditablePrimarySubnetPurpose] =
		useState(savedPrimarySubnetPurpose)

	const savedCommonNetworkResources = useMemo(
		() => normalizeCommonNetworkResources(details.commonNetworkResources),
		[details.commonNetworkResources]
	)
	// Keep VPCs saved on older requests even if they drop off the constant list.
	const commonNetworkVpcNames = useMemo(
		() =>
			Array.from(
				new Set([
					...CONSTANTS.COMMON_NETWORK_VPCS,
					...savedCommonNetworkResources.map((resource) => resource.vpcName)
				])
			),
		[savedCommonNetworkResources]
	)
	const [commonNetworkRequired, setCommonNetworkRequired] = useState<
		'yes' | 'no'
	>(savedCommonNetworkResources.length > 0 ? 'yes' : 'no')
	const [commonNetworkVpcs, setCommonNetworkVpcs] = useState<
		Record<string, CommonNetworkVpcState>
	>(() =>
		Object.fromEntries(
			commonNetworkVpcNames.map((vpcName) => {
				const saved = savedCommonNetworkResources.find(
					(resource) => resource.vpcName === vpcName
				)
				return [
					vpcName,
					{
						enabled: Boolean(saved),
						expanded: Boolean(saved),
						subnets: saved ? saved.subnets : []
					}
				]
			})
		)
	)
	// Memoized so the approval store sync below doesn't loop on a fresh array.
	const enabledCommonNetworkResources = useMemo<CommonNetworkResource[]>(
		() =>
			commonNetworkVpcNames
				.filter((vpcName) => commonNetworkVpcs[vpcName]?.enabled)
				.map((vpcName) => ({
					vpcName,
					subnets: commonNetworkVpcs[vpcName].subnets
				})),
		[commonNetworkVpcNames, commonNetworkVpcs]
	)

	const {
		actions: { setFormValues }
	} = useApprovalFormStore()

	const defaultValues = useMemo(
		() => ({
			primarySubnet:
				typeof details.primarySubnet === 'string' ? details.primarySubnet : '',
			controlPlaneSubnet:
				typeof details.controlPlaneSubnet === 'string'
					? details.controlPlaneSubnet
					: '',
			secondaryPodsRange:
				typeof details.secondaryPodsRange === 'string'
					? details.secondaryPodsRange
					: '',
			secondaryServicesRange:
				typeof details.secondaryServicesRange === 'string'
					? details.secondaryServicesRange
					: ''
		}),
		[
			details.controlPlaneSubnet,
			details.primarySubnet,
			details.secondaryPodsRange,
			details.secondaryServicesRange
		]
	)

	const { control, reset, watch } = useForm<SubnetFormValues>({
		mode: 'onChange',
		reValidateMode: 'onChange',
		defaultValues
	})

	const watchedPrimarySubnet = watch('primarySubnet')
	const watchedControlPlaneSubnet = watch('controlPlaneSubnet')
	const watchedSecondaryPodsRange = watch('secondaryPodsRange')
	const watchedSecondaryServicesRange = watch('secondaryServicesRange')
	const primarySubnetLabel = isGke ? 'Node Subnet Range' : 'Primary Subnet'
	const showNonGkeSubnetFields = isGke || nonGkeAddSubnets === 'yes'

	const syncApprovalFormValues = useCallback(() => {
		setFormValues('primarySubnet', watchedPrimarySubnet)
		setFormValues('controlPlaneSubnet', watchedControlPlaneSubnet)
		setFormValues('secondaryPodsRange', watchedSecondaryPodsRange)
		setFormValues('secondaryServicesRange', watchedSecondaryServicesRange)
		setFormValues(
			'primarySubnetResourceType',
			editablePrimarySubnetResourceType
		)
		setFormValues('primarySubnetPurpose', editablePrimarySubnetPurpose)
		setFormValues('additionalSubnets', editableAdditionalSubnets)
		// Always written so a previous request's values never leak into this one.
		setFormValues('commonNetworkInherited', isCommonNetworkInherited)
		setFormValues(
			'commonNetworkLoading',
			developCommonNetwork.status === 'loading'
		)
		setFormValues('developRequestMissing', isDevelopRequestMissing)
		if (!isGke) {
			setFormValues('nonGkeAddSubnets', nonGkeAddSubnets)
		}
		if (isCommonNetworkInherited) {
			setFormValues(
				'commonNetworkRequired',
				developCommonNetwork.resources.length > 0 ? 'yes' : 'no'
			)
			setFormValues('commonNetworkResources', developCommonNetwork.resources)
		} else {
			setFormValues('commonNetworkRequired', commonNetworkRequired)
			setFormValues('commonNetworkResources', enabledCommonNetworkResources)
		}
	}, [
		commonNetworkRequired,
		developCommonNetwork,
		editableAdditionalSubnets,
		editablePrimarySubnetPurpose,
		editablePrimarySubnetResourceType,
		enabledCommonNetworkResources,
		isCommonNetworkInherited,
		isDevelopRequestMissing,
		isGke,
		nonGkeAddSubnets,
		setFormValues,
		watchedControlPlaneSubnet,
		watchedPrimarySubnet,
		watchedSecondaryPodsRange,
		watchedSecondaryServicesRange
	])

	const clearNonGkeSubnetFields = () => {
		reset({
			primarySubnet: '',
			controlPlaneSubnet: '',
			secondaryPodsRange: '',
			secondaryServicesRange: ''
		})
		setEditableAdditionalSubnets([])
		setEditablePrimarySubnetResourceType('')
		setEditablePrimarySubnetPurpose('')
		setFormValues('primarySubnet', '')
		setFormValues('controlPlaneSubnet', '')
		setFormValues('secondaryPodsRange', '')
		setFormValues('secondaryServicesRange', '')
		setFormValues('primarySubnetResourceType', '')
		setFormValues('primarySubnetPurpose', '')
		setFormValues('additionalSubnets', [])
	}

	const handleNonGkeAddSubnetsChange = (value: 'yes' | 'no') => {
		setNonGkeAddSubnets(value)
		setFormValues('nonGkeAddSubnets', value)
		if (value === 'no') {
			clearNonGkeSubnetFields()
		}
	}

	useEffect(() => {
		if (!embeddedInApproval) {
			return
		}
		syncApprovalFormValues()
	}, [embeddedInApproval, syncApprovalFormValues])

	useEffect(() => {
		if (!embeddedInApproval || !onSubnetValidityChange) {
			return
		}

		onSubnetValidityChange(
			validateSubnetDetailsForApproval({
				infrastructureType,
				nonGkeAddSubnets,
				primarySubnet: watchedPrimarySubnet,
				controlPlaneSubnet: watchedControlPlaneSubnet,
				secondaryPodsRange: watchedSecondaryPodsRange,
				secondaryServicesRange: watchedSecondaryServicesRange,
				primarySubnetResourceType: editablePrimarySubnetResourceType,
				primarySubnetPurpose: editablePrimarySubnetPurpose,
				additionalSubnets: editableAdditionalSubnets,
				commonNetworkRequired,
				commonNetworkResources: enabledCommonNetworkResources,
				commonNetworkInherited: isCommonNetworkInherited,
				developRequestMissing: isDevelopRequestMissing
			})
		)
	}, [
		commonNetworkRequired,
		editableAdditionalSubnets,
		editablePrimarySubnetPurpose,
		editablePrimarySubnetResourceType,
		embeddedInApproval,
		enabledCommonNetworkResources,
		infrastructureType,
		isCommonNetworkInherited,
		isDevelopRequestMissing,
		nonGkeAddSubnets,
		onSubnetValidityChange,
		watchedControlPlaneSubnet,
		watchedPrimarySubnet,
		watchedSecondaryPodsRange,
		watchedSecondaryServicesRange
	])

	const updateCommonNetworkVpc = (
		vpcName: string,
		update: (vpc: CommonNetworkVpcState) => CommonNetworkVpcState
	) => {
		setCommonNetworkVpcs((previous) => ({
			...previous,
			[vpcName]: update(previous[vpcName] || disabledCommonNetworkVpc)
		}))
	}

	const updateCommonNetworkSubnet = (
		vpcName: string,
		index: number,
		changes: Partial<CommonNetworkSubnet>
	) => {
		updateCommonNetworkVpc(vpcName, (vpc) => ({
			...vpc,
			subnets: vpc.subnets.map((subnet, subnetIndex) =>
				subnetIndex === index ? { ...subnet, ...changes } : subnet
			)
		}))
	}

	const handleCommonNetworkVpcToggle = (vpcName: string, enabled: boolean) => {
		updateCommonNetworkVpc(vpcName, (vpc) => ({
			enabled,
			expanded: enabled,
			subnets:
				enabled && vpc.subnets.length === 0
					? [emptyCommonNetworkSubnet()]
					: vpc.subnets
		}))
	}

	const renderReadOnlyField = (label: string, value: string) => (
		<div>
			<Box variant="awsui-key-label">{label}</Box>
			{value || 'NA'}
		</div>
	)

	// Purpose applies to PSA/PSC only; other resource types save "N/A".
	const renderPurposeField = ({
		resourceType,
		value,
		errorText,
		description,
		onChange
	}: {
		resourceType: string
		value: string
		errorText?: string
		description?: string
		onChange: (purpose: string) => void
	}) => {
		const showPurpose = requiresSubnetPurpose(resourceType)
		return (
			<FormField
				label="Purpose"
				description={description}
				constraintText={showPurpose ? 'Pick one or type your own' : undefined}
				errorText={errorText}
			>
				{showPurpose ? (
					<Autosuggest
						value={value}
						options={
							isPsaResourceType(resourceType)
								? psaPurposeOptions
								: pscPurposeOptions
						}
						enteredTextLabel={(entered) => `Use "${entered}"`}
						placeholder="Select or enter a purpose"
						empty="No matching purposes"
						onChange={({ detail }) => onChange(detail.value)}
					/>
				) : (
					<Box color="text-body-secondary" padding={{ top: 'xxs' }}>
						N/A (only for PSA or PSC)
					</Box>
				)}
			</FormField>
		)
	}

	const renderAdditionalSubnetsEdit = (optional = false) => (
		<SpaceBetween direction="vertical" size="s">
			<Box variant="awsui-key-label">
				Additional Subnets{optional ? ' (optional)' : ''}
			</Box>
			{editableAdditionalSubnets.map((subnet, index) => {
				const cidrError =
					subnet.cidr.trim() !== ''
						? getSubnetCidrError(
								subnet.cidr,
								subnet.resourceType,
								`Additional Subnet ${index + 1}`
						  )
						: undefined
				const resourceTypeError =
					subnet.cidr.trim() !== '' && subnet.resourceType.trim() === ''
						? 'Resource type is required'
						: undefined

				return (
					<div
						key={`additional-subnet-${index}`}
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr 1fr 1fr auto',
							gap: '12px',
							alignItems: 'start'
						}}
					>
						<FormField
							label={`Additional Subnet ${index + 1}`}
							description="Optional additional subnet CIDR range"
							constraintText="Format: CIDR notation (e.g., 10.0.1.0/24)"
							errorText={cidrError}
						>
							<Input
								value={subnet.cidr}
								placeholder="e.g., 10.0.1.0/24"
								onChange={({ detail }) => {
									const next = [...editableAdditionalSubnets]
									next[index] = { ...next[index], cidr: detail.value }
									setEditableAdditionalSubnets(next)
								}}
							/>
						</FormField>
						<FormField
							label="Resource Type"
							description="Select the GCP resource type"
							errorText={resourceTypeError}
						>
							<Select
								selectedOption={
									subnet.resourceType
										? resourceTypeOptions.find(
												(o) => o.value === subnet.resourceType
										  ) || null
										: null
								}
								options={resourceTypeOptions}
								placeholder="Select resource type"
								filteringType="auto"
								onChange={({ detail }) => {
									const resourceType = detail.selectedOption.value || ''
									const next = [...editableAdditionalSubnets]
									next[index] = {
										...next[index],
										resourceType,
										purpose: requiresSubnetPurpose(resourceType)
											? next[index].purpose
											: ''
									}
									setEditableAdditionalSubnets(next)
								}}
							/>
						</FormField>
						{renderPurposeField({
							resourceType: subnet.resourceType,
							value: subnet.purpose || '',
							description: 'Only for PSA or PSC',
							errorText:
								subnet.cidr.trim() !== ''
									? getSubnetPurposeError(subnet.resourceType, subnet.purpose)
									: undefined,
							onChange: (purpose) => {
								const next = [...editableAdditionalSubnets]
								next[index] = { ...next[index], purpose }
								setEditableAdditionalSubnets(next)
							}
						})}
						{renderRowAction(
							<Button
								variant="link"
								iconName="remove"
								ariaLabel={`Remove additional subnet ${index + 1}`}
								onClick={() => {
									setEditableAdditionalSubnets(
										editableAdditionalSubnets.filter(
											(_, itemIndex) => itemIndex !== index
										)
									)
								}}
							/>,
							true
						)}
					</div>
				)
			})}
			<Button
				iconName="add-plus"
				onClick={() => {
					setEditableAdditionalSubnets([
						...editableAdditionalSubnets,
						{ cidr: '', resourceType: '', purpose: '' }
					])
				}}
			>
				Add Additional Subnet
			</Button>
		</SpaceBetween>
	)

	// One level below the container's h3 title.
	const renderSectionHeading = (title: string, description?: string) => (
		<div>
			<Box variant="h4" padding="n">
				{title}
			</Box>
			{description && (
				<Box color="text-body-secondary" fontSize="body-s">
					{description}
				</Box>
			)}
		</div>
	)

	const renderCommonNetworkSubnetRow = (
		vpcName: string,
		subnet: CommonNetworkSubnet,
		index: number
	) => {
		const label = `Subnet ${index + 1}`
		const errors = getCommonNetworkSubnetErrors(subnet, label)

		return (
			<div
				key={`${vpcName}-subnet-${index}`}
				style={{
					display: 'grid',
					gridTemplateColumns: '1fr 1fr 1fr auto',
					gap: '12px',
					alignItems: 'start'
				}}
			>
				<FormField
					label={label}
					constraintText="Format: CIDR notation (e.g., 10.0.1.0/28)"
					errorText={errors.cidr}
				>
					<Input
						value={subnet.cidr}
						placeholder="e.g., 10.0.1.0/28"
						onChange={({ detail }) =>
							updateCommonNetworkSubnet(vpcName, index, { cidr: detail.value })
						}
					/>
				</FormField>
				<FormField label="Resource Type" errorText={errors.resourceType}>
					<Select
						selectedOption={
							subnet.resourceType
								? commonNetworkResourceTypeOptions.find(
										(o) => o.value === subnet.resourceType
								  ) || null
								: null
						}
						options={commonNetworkResourceTypeOptions}
						placeholder="Select resource type"
						onChange={({ detail }) => {
							const resourceType = detail.selectedOption.value || ''
							updateCommonNetworkSubnet(vpcName, index, {
								resourceType,
								purpose: requiresSubnetPurpose(resourceType)
									? subnet.purpose
									: ''
							})
						}}
					/>
				</FormField>
				{renderPurposeField({
					resourceType: subnet.resourceType,
					value: subnet.purpose,
					errorText: errors.purpose,
					onChange: (purpose) =>
						updateCommonNetworkSubnet(vpcName, index, { purpose })
				})}
				{renderRowAction(
					<Button
						variant="link"
						iconName="remove"
						ariaLabel={`Remove subnet ${index + 1} from ${vpcName}`}
						onClick={() =>
							updateCommonNetworkVpc(vpcName, (vpc) => ({
								...vpc,
								subnets: vpc.subnets.filter(
									(_, subnetIndex) => subnetIndex !== index
								)
							}))
						}
					/>
				)}
			</div>
		)
	}

	const renderCommonNetworkVpc = (vpcName: string) => {
		const vpc = commonNetworkVpcs[vpcName] || disabledCommonNetworkVpc
		const completeCount = countCompleteCommonNetworkSubnets(vpc.subnets)

		return (
			<ExpandableSection
				key={vpcName}
				variant="container"
				expanded={vpc.expanded}
				onChange={({ detail }) =>
					updateCommonNetworkVpc(vpcName, (current) => ({
						...current,
						expanded: detail.expanded
					}))
				}
				headingTagOverride="h5"
				headerText={
					<Box variant="span" fontSize="heading-xs" fontWeight="bold">
						{vpcName}
					</Box>
				}
				headerAriaLabel={vpcName}
				headerDescription={
					vpc.enabled
						? `${completeCount} of ${vpc.subnets.length} subnet(s) complete`
						: 'Not needed'
				}
				headerActions={
					<Toggle
						checked={vpc.enabled}
						onChange={({ detail }) =>
							handleCommonNetworkVpcToggle(vpcName, detail.checked)
						}
					>
						Needed
					</Toggle>
				}
			>
				{vpc.enabled ? (
					<SpaceBetween direction="vertical" size="s">
						{vpc.subnets.map((subnet, index) =>
							renderCommonNetworkSubnetRow(vpcName, subnet, index)
						)}
						{completeCount === 0 && (
							<Box color="text-status-error" fontSize="body-s">
								Add at least one complete subnet to approve this request.
							</Box>
						)}
						<Button
							iconName="add-plus"
							onClick={() =>
								updateCommonNetworkVpc(vpcName, (current) => ({
									...current,
									subnets: [...current.subnets, emptyCommonNetworkSubnet()]
								}))
							}
						>
							Add Subnet
						</Button>
					</SpaceBetween>
				) : (
					<Box color="text-body-secondary">
						Turn on Needed to add subnets under this VPC.
					</Box>
				)}
			</ExpandableSection>
		)
	}

	const renderCommonNetworkEdit = () => (
		<SpaceBetween direction="vertical" size="l">
			{renderSectionHeading(
				'Common Network',
				'Subnets this project needs in the shared common VPCs.'
			)}
			<FormField
				label="Do you need Common Network Resources?"
				errorText={
					commonNetworkRequired === 'yes' &&
					enabledCommonNetworkResources.length === 0
						? 'Turn on at least one common VPC, or answer No.'
						: undefined
				}
			>
				<RadioGroup
					value={commonNetworkRequired}
					items={[
						{ value: 'no', label: 'No' },
						{ value: 'yes', label: 'Yes' }
					]}
					onChange={({ detail }) =>
						setCommonNetworkRequired(detail.value as 'yes' | 'no')
					}
				/>
			</FormField>
			{commonNetworkRequired === 'yes' && (
				<SpaceBetween direction="vertical" size="m">
					{commonNetworkVpcNames.map(renderCommonNetworkVpc)}
				</SpaceBetween>
			)}
		</SpaceBetween>
	)

	const renderInheritedCommonNetworkVpc = (resource: CommonNetworkResource) => (
		<ExpandableSection
			key={resource.vpcName}
			variant="container"
			defaultExpanded
			headingTagOverride="h5"
			headerText={
				<Box variant="span" fontSize="heading-xs" fontWeight="bold">
					{resource.vpcName}
				</Box>
			}
			headerAriaLabel={resource.vpcName}
			headerDescription={`${resource.subnets.length} subnet(s)`}
			headerActions={
				<Toggle checked disabled>
					Needed
				</Toggle>
			}
		>
			<SpaceBetween direction="vertical" size="s">
				{resource.subnets.map((subnet, index) => (
					<div
						key={`${resource.vpcName}-inherited-${index}`}
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr 1fr 1fr 1.4fr',
							gap: '12px',
							alignItems: 'start'
						}}
					>
						<FormField label={`Subnet ${index + 1}`}>
							<Input value={subnet.cidr} disabled />
						</FormField>
						<FormField label="Resource Type">
							<Select
								selectedOption={{
									label: subnet.resourceType,
									value: subnet.resourceType
								}}
								options={resourceTypeOptions}
								disabled
							/>
						</FormField>
						<FormField label="Purpose">
							<Input value={subnet.purpose} disabled />
						</FormField>
						{/* Only Staging/Production show the name; Develop has none until approval. */}
						<FormField label="Subnet Name">
							<Input
								value={subnet.subnetName || ''}
								placeholder="Not generated yet"
								disabled
							/>
						</FormField>
					</div>
				))}
			</SpaceBetween>
		</ExpandableSection>
	)

	// Staging/Production: greyed out, filled from the approved Develop request.
	const renderCommonNetworkInherited = () => {
		const { status, developProjectName, resources } = developCommonNetwork
		const developName = developProjectName || 'the Develop request'

		let notice: ReactNode
		if (status === 'loading') {
			notice = (
				<StatusIndicator type="loading">
					Looking up the Develop request…
				</StatusIndicator>
			)
		} else if (status === 'not_found') {
			notice = (
				<Alert type="error">
					No approved Develop request found for <strong>{developName}</strong>.
					Approve the Develop request first; Staging and Production requests
					can&apos;t be approved without it.
				</Alert>
			)
		} else if (resources.length === 0) {
			notice = (
				<Alert type="info">
					The Develop request <strong>{developName}</strong> has no Common
					Network Resources, so none are inherited.
				</Alert>
			)
		} else {
			notice = (
				<Alert type="info">
					Inherited from the Develop request <strong>{developName}</strong>. To
					change it, update the Develop request.
				</Alert>
			)
		}

		const answer =
			status === 'found' ? (resources.length > 0 ? 'yes' : 'no') : null

		return (
			<SpaceBetween direction="vertical" size="l">
				{renderSectionHeading(
					'Common Network',
					'Inherited from the Develop request. Read-only here.'
				)}
				{notice}
				<FormField label="Do you need Common Network Resources?">
					<RadioGroup
						value={answer}
						items={[
							{ value: 'no', label: 'No', disabled: true },
							{ value: 'yes', label: 'Yes', disabled: true }
						]}
					/>
				</FormField>
				{status === 'found' && resources.length > 0 && (
					<SpaceBetween direction="vertical" size="m">
						{resources.map(renderInheritedCommonNetworkVpc)}
					</SpaceBetween>
				)}
			</SpaceBetween>
		)
	}

	const renderCommonNetworkReadOnly = () => (
		<SpaceBetween direction="vertical" size="m">
			{renderSectionHeading(
				'Common Network',
				inheritsFromDevelop ? 'Inherited from the Develop request.' : undefined
			)}
			{savedCommonNetworkResources.length > 0 ? (
				savedCommonNetworkResources.map((resource) => (
					<div key={resource.vpcName}>
						<Box variant="awsui-key-label">{resource.vpcName}</Box>
						<Table
							variant="embedded"
							items={resource.subnets}
							columnDefinitions={[
								{
									id: 'cidr',
									header: 'Subnet CIDR',
									cell: (subnet) => subnet.cidr
								},
								{
									id: 'resourceType',
									header: 'Resource Type',
									cell: (subnet) => subnet.resourceType || 'NA'
								},
								{
									id: 'purpose',
									header: 'Purpose',
									cell: (subnet) => subnet.purpose || 'N/A'
								},
								// Staging/Production only: the name copied from the Develop request.
								...(inheritsFromDevelop
									? [
											{
												id: 'subnetName',
												header: 'Subnet Name',
												cell: (subnet: CommonNetworkSubnet) =>
													subnet.subnetName || 'Not generated yet'
											}
									  ]
									: [])
							]}
						/>
					</div>
				))
			) : (
				<Box color="text-body-secondary" fontSize="body-s">
					No common network resources requested.
				</Box>
			)}
		</SpaceBetween>
	)

	const renderGkeFields = () => (
		<>
			<FormInput
				name="primarySubnet"
				label={primarySubnetLabel}
				placeholder="e.g., 10.0.0.0/24"
				constraintText="Format: CIDR notation (e.g., 10.0.0.0/24)"
				control={control}
				rules={cidrRules(primarySubnetLabel)}
				setFormValues={setFormValues}
			/>
			<FormInput
				name="controlPlaneSubnet"
				label="Control Plane Subnet"
				placeholder="e.g., 10.0.1.0/28"
				constraintText="Format: CIDR notation (e.g., 10.0.1.0/28)"
				control={control}
				rules={cidrRules('Control Plane Subnet')}
				setFormValues={setFormValues}
			/>
			<FormInput
				name="secondaryPodsRange"
				label="Secondary Range for Pods"
				placeholder="e.g., 10.1.0.0/16"
				constraintText="Format: CIDR notation (e.g., 10.1.0.0/16)"
				control={control}
				rules={cidrRules('Secondary Range for Pods')}
				setFormValues={setFormValues}
			/>
			<FormInput
				name="secondaryServicesRange"
				label="Secondary Range for Services"
				placeholder="e.g., 10.2.0.0/20"
				constraintText="Format: CIDR notation (e.g., 10.2.0.0/20)"
				control={control}
				rules={cidrRules('Secondary Range for Services')}
				setFormValues={setFormValues}
			/>
			{renderAdditionalSubnetsEdit()}
		</>
	)

	const renderNonGkeFields = () => (
		<>
			<FormField label="Do you want to add subnet details?">
				<RadioGroup
					value={nonGkeAddSubnets}
					items={[
						{ value: 'no', label: 'No' },
						{ value: 'yes', label: 'Yes' }
					]}
					onChange={({ detail }) => {
						handleNonGkeAddSubnetsChange(detail.value as 'yes' | 'no')
					}}
				/>
			</FormField>
			{showNonGkeSubnetFields && (
				<>
					<div
						style={{
							display: 'grid',
							gridTemplateColumns: '1fr 1fr 1fr',
							gap: '12px',
							alignItems: 'start'
						}}
					>
						<FormInput
							name="primarySubnet"
							label={primarySubnetLabel}
							placeholder="e.g., 10.0.0.0/24"
							constraintText="Format: CIDR notation (e.g., 10.0.0.0/24)"
							control={control}
							rules={cidrRules(
								primarySubnetLabel,
								true,
								editablePrimarySubnetResourceType
							)}
							setFormValues={setFormValues}
						/>
						<FormField
							label="Resource Type"
							constraintText="Select the GCP resource type for this subnet"
							errorText={
								(watchedPrimarySubnet || '').trim() !== '' &&
								!editablePrimarySubnetResourceType
									? 'Resource type is required'
									: undefined
							}
						>
							<Select
								selectedOption={
									editablePrimarySubnetResourceType
										? resourceTypeOptions.find(
												(o) => o.value === editablePrimarySubnetResourceType
										  ) || null
										: null
								}
								options={resourceTypeOptions}
								placeholder="Select resource type"
								filteringType="auto"
								onChange={({ detail }) => {
									const resourceType = detail.selectedOption.value || ''
									setEditablePrimarySubnetResourceType(resourceType)
									if (!requiresSubnetPurpose(resourceType)) {
										setEditablePrimarySubnetPurpose('')
									}
								}}
							/>
						</FormField>
						{renderPurposeField({
							resourceType: editablePrimarySubnetResourceType,
							value: editablePrimarySubnetPurpose,
							errorText:
								(watchedPrimarySubnet || '').trim() !== ''
									? getSubnetPurposeError(
											editablePrimarySubnetResourceType,
											editablePrimarySubnetPurpose
									  )
									: undefined,
							onChange: setEditablePrimarySubnetPurpose
						})}
					</div>
					{renderAdditionalSubnetsEdit(true)}
				</>
			)}
		</>
	)

	const renderReadOnlyView = () => (
		<ColumnLayout columns={2} variant="text-grid">
			{hasSavedSubnets || isGke ? (
				<>
					{renderReadOnlyField(primarySubnetLabel, defaultValues.primarySubnet)}
					{!isGke &&
						renderReadOnlyField(
							'Primary Subnet Resource Type',
							savedPrimarySubnetResourceType || 'NA'
						)}
					{!isGke &&
						renderReadOnlyField(
							'Primary Subnet Purpose',
							savedPrimarySubnetPurpose || 'N/A'
						)}
					{isGke && (
						<>
							{renderReadOnlyField(
								'Control Plane Subnet',
								defaultValues.controlPlaneSubnet
							)}
							{renderReadOnlyField(
								'Secondary Range for Pods',
								defaultValues.secondaryPodsRange
							)}
							{renderReadOnlyField(
								'Secondary Range for Services',
								defaultValues.secondaryServicesRange
							)}
						</>
					)}
					{savedAdditionalSubnets.length > 0 && (
						<div>
							<Box variant="awsui-key-label">Additional Subnets</Box>
							{savedAdditionalSubnets.map((subnet, index) => (
								<div key={index}>
									{subnet.cidr}
									{subnet.resourceType
										? ` (${[
												subnet.resourceType,
												requiresSubnetPurpose(subnet.resourceType)
													? subnet.purpose
													: ''
										  ]
												.filter(Boolean)
												.join(' · ')})`
										: ''}
								</div>
							))}
						</div>
					)}
				</>
			) : (
				<Box color="text-body-secondary" fontSize="body-s">
					No subnet details configured.
				</Box>
			)}
		</ColumnLayout>
	)

	const renderSummary = () => (
		<ColumnLayout key="summary" columns={2} variant="text-grid">
			{renderReadOnlyField('Environment', environment)}
			{renderReadOnlyField('Infrastructure Type', infrastructureType)}
		</ColumnLayout>
	)

	const renderEnvironmentNetwork = (content: ReactNode) => (
		<SpaceBetween key="environment-network" direction="vertical" size="l">
			{renderSectionHeading(
				environmentNetworkHeading,
				environment ? `Subnets in ${environment.toLowerCase()} VPC.` : undefined
			)}
			{content}
		</SpaceBetween>
	)

	const sections = embeddedInApproval
		? [
				renderSummary(),
				renderEnvironmentNetwork(
					<SpaceBetween direction="vertical" size="l">
						{isGke ? renderGkeFields() : renderNonGkeFields()}
					</SpaceBetween>
				),
				<div key="common-network">
					{isCommonNetworkInherited
						? renderCommonNetworkInherited()
						: renderCommonNetworkEdit()}
				</div>
		  ]
		: [
				renderSummary(),
				renderEnvironmentNetwork(renderReadOnlyView()),
				<div key="common-network">{renderCommonNetworkReadOnly()}</div>
		  ]

	return (
		<Container header={<Header variant="h3">Subnet Details</Header>}>
			<ColumnLayout columns={1} borders="horizontal">
				{sections}
			</ColumnLayout>
		</Container>
	)
}

export default GcpProjectSubnetDetails
